import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BrandMark } from "@/components/landing/BrandMark";

const NAV_LINKS = [
  { key: "start", href: "/#top" },
  { key: "personalization", href: "/#personalization" },
  { key: "bulk", href: "/#bulk" },
  { key: "reputation", href: "/#reputation" },
  { key: "faq", href: "/#faq" },
  { key: "contact", href: "/#contact" },
  { key: "bestseller", href: "/#bestseller" },
];

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-brand-mist" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20 gap-4">
        <div className="flex items-center gap-x-8 xl:gap-x-12">
          <BrandMark />

          <nav className="hidden lg:flex items-center gap-x-5 xl:gap-x-7 flex-shrink-0" aria-label="primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                className="text-sm whitespace-nowrap text-brand-graphite hover:text-brand-ink link-underline transition-colors"
                data-testid={`nav-${l.key}`}
              >
                {t.nav[l.key]}
              </a>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <LangSwitcher lang={lang} setLang={setLang} />
          <a
            href="/#contact"
            className="hidden xl:inline-flex btn-primary py-2.5 px-5 text-[13px]"
            data-testid="header-cta-button"
          >
            {t.nav.cta}
          </a>
          <button
            type="button"
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-brand-ink/15 text-brand-ink hover:border-brand-ink transition-colors"
            onClick={() => setOpen(true)}
            aria-label={t.nav.menu}
            data-testid="mobile-menu-open-button"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
        data-testid="mobile-menu"
      >
        <div
          className="absolute inset-0 bg-brand-ink/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[86%] max-w-sm bg-white shadow-2xl transition-transform duration-500 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between h-16 px-6 border-b border-brand-mist">
            <BrandMark />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="grid place-items-center h-10 w-10 rounded-full border border-brand-ink/15 text-brand-ink"
              aria-label={t.nav.close}
              data-testid="mobile-menu-close-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="p-6 flex flex-col" aria-label="mobile">
            {NAV_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-4 border-b border-brand-mist text-lg font-medium text-brand-ink"
                data-testid={`mobile-nav-${l.key}`}
              >
                {t.nav[l.key]}
              </a>
            ))}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              className="btn-primary mt-8 w-full justify-center"
              data-testid="mobile-cta-button"
            >
              {t.nav.cta}
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}

function LangSwitcher({ lang, setLang }) {
  return (
    <div
      className="flex items-center rounded-full border border-brand-ink/15 bg-white/70 backdrop-blur p-0.5 text-[11px] font-semibold tracking-wider"
      role="group"
      aria-label="Language"
      data-testid="lang-switcher"
    >
      {["de", "en"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full transition-all duration-300 ${
            lang === l ? "bg-brand-ink text-white" : "text-brand-graphite hover:text-brand-ink"
          }`}
          data-testid={`lang-switch-${l}`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
